import { createSlice } from "@reduxjs/toolkit"
import {
  checkGuestEmail,
  checkGuestStatus,
  createGuestBooking,
  createImageSpec,
  createTemporaryGuest,
  deleteImageSpec,
  extendGuestExpiration,
  fetchAllImageSpecs,
  fetchAllTemporaryGuests,
  partialUpdateImageSpec,
  retrieveImageSpec,
  updateImageSpec,
  verifyGuestEmail
} from "../actions/external/external-action"

const initialState = {
  guests: [],
  imageSpecs: [],
  totalGuests: 0,
  activeGuests: 0,
  expiredGuests: 0,
  currentGuest: null,
  currentImageSpec: null,
  loading: false,
  error: null,
  lastAction: null
}

const externalManagementSlice = createSlice({
  name: "external",
  initialState,
  reducers: {
    clearCurrentGuest: (state) => {
      state.currentGuest = null
    },
    clearCurrentImageSpec: (state) => {
      state.currentImageSpec = null
    },
    clearErrors: (state) => {
      state.error = null
    },
    setLastAction: (state, action) => {
      state.lastAction = action.payload
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchAllTemporaryGuests.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "FETCH_ALL_PENDING"
      })
      .addCase(fetchAllTemporaryGuests.fulfilled, (state, action) => {
        state.loading = false
        state.guests = action.payload.guests
        state.totalGuests = action.payload.total_guests
        state.activeGuests = action.payload.active_guests
        state.expiredGuests = action.payload.expired_guests
        state.lastAction = "FETCH_ALL_FULFILLED"
      })
      .addCase(fetchAllTemporaryGuests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "FETCH_ALL_REJECTED"
      })

      // Create temporary guest
      .addCase(createTemporaryGuest.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "CREATE_GUEST_PENDING"
      })
      .addCase(createTemporaryGuest.fulfilled, (state, action) => {
        state.loading = false
        state.currentGuest = action.payload.guest
        state.lastAction = "CREATE_GUEST_FULFILLED"
      })
      .addCase(createTemporaryGuest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "CREATE_GUEST_REJECTED"
      })

      .addCase(verifyGuestEmail.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "VERIFY_EMAIL_PENDING"
      })
      .addCase(verifyGuestEmail.fulfilled, (state, action) => {
        state.loading = false
        state.currentGuest = action.payload.guest
        state.lastAction = "VERIFY_EMAIL_FULFILLED"
      })
      .addCase(verifyGuestEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "VERIFY_EMAIL_REJECTED"
      })

      // Check guest status
      .addCase(checkGuestStatus.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "CHECK_STATUS_PENDING"
      })
      .addCase(checkGuestStatus.fulfilled, (state, action) => {
        state.loading = false
        state.currentGuest = action.payload
        state.lastAction = "CHECK_STATUS_FULFILLED"
      })
      .addCase(checkGuestStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "CHECK_STATUS_REJECTED"
      })

      // Create guest booking
      .addCase(createGuestBooking.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "CREATE_BOOKING_PENDING"
      })
      .addCase(createGuestBooking.fulfilled, (state) => {
        state.loading = false
        state.lastAction = "CREATE_BOOKING_FULFILLED"
      })
      .addCase(createGuestBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "CREATE_BOOKING_REJECTED"
      })

      // Extend guest expiration
      .addCase(extendGuestExpiration.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "EXTEND_EXPIRATION_PENDING"
      })
      .addCase(extendGuestExpiration.fulfilled, (state, action) => {
        state.loading = false
        state.lastAction = "EXTEND_EXPIRATION_FULFILLED"

        if (
          state.currentGuest &&
          state.currentGuest.guest_reference ===
            action.payload.guest.guest_reference
        ) {
          state.currentGuest = action.payload.guest
        }

        state.guests = state.guests.map((guest) =>
          guest.guest_reference === action.payload.guest.guest_reference
            ? action.payload.guest
            : guest
        )
      })
      .addCase(extendGuestExpiration.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "EXTEND_EXPIRATION_REJECTED"
      })

      .addCase(checkGuestEmail.pending, (state) => {
        state.loading = true
        state.error = null
        state.emailExists = null
        state.lastAction = "CHECK_EMAIL_PENDING"
      })
      .addCase(checkGuestEmail.fulfilled, (state, action) => {
        state.loading = false
        state.emailExists = action.payload.exists

        if (action.payload.exists && action.payload.guest) {
          state.currentGuest = action.payload.guest
          state.emailVerified = action.payload.guest.is_verified
        }

        state.lastAction = "CHECK_EMAIL_FULFILLED"
      })
      .addCase(checkGuestEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.emailExists = false
        state.lastAction = "CHECK_EMAIL_REJECTED"
      })

      .addCase(fetchAllImageSpecs.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "FETCH_ALL_IMAGE_SPECS_PENDING"
      })
      .addCase(fetchAllImageSpecs.fulfilled, (state, action) => {
        state.loading = false
        state.imageSpecs = action.payload
        state.lastAction = "FETCH_ALL_IMAGE_SPECS_FULFILLED"
      })
      .addCase(fetchAllImageSpecs.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "FETCH_ALL_IMAGE_SPECS_REJECTED"
      })

      .addCase(createImageSpec.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "CREATE_IMAGE_SPEC_PENDING"
      })
      .addCase(createImageSpec.fulfilled, (state, action) => {
        state.loading = false
        state.imageSpecs = [...state.imageSpecs, action.payload]
        state.currentImageSpec = action.payload
        state.lastAction = "CREATE_IMAGE_SPEC_FULFILLED"
      })
      .addCase(createImageSpec.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "CREATE_IMAGE_SPEC_REJECTED"
      })

      .addCase(retrieveImageSpec.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "RETRIEVE_IMAGE_SPEC_PENDING"
      })
      .addCase(retrieveImageSpec.fulfilled, (state, action) => {
        state.loading = false
        state.currentImageSpec = action.payload
        state.lastAction = "RETRIEVE_IMAGE_SPEC_FULFILLED"
      })
      .addCase(retrieveImageSpec.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "RETRIEVE_IMAGE_SPEC_REJECTED"
      })

      .addCase(updateImageSpec.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "UPDATE_IMAGE_SPEC_PENDING"
      })
      .addCase(updateImageSpec.fulfilled, (state, action) => {
        state.loading = false
        state.currentImageSpec = action.payload
        state.imageSpecs = state.imageSpecs.map((spec) =>
          spec.id === action.payload.id ? action.payload : spec
        )
        state.lastAction = "UPDATE_IMAGE_SPEC_FULFILLED"
      })
      .addCase(updateImageSpec.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "UPDATE_IMAGE_SPEC_REJECTED"
      })

      .addCase(partialUpdateImageSpec.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "PARTIAL_UPDATE_IMAGE_SPEC_PENDING"
      })
      .addCase(partialUpdateImageSpec.fulfilled, (state, action) => {
        state.loading = false
        state.currentImageSpec = action.payload
        state.imageSpecs = state.imageSpecs.map((spec) =>
          spec.id === action.payload.id ? action.payload : spec
        )
        state.lastAction = "PARTIAL_UPDATE_IMAGE_SPEC_FULFILLED"
      })
      .addCase(partialUpdateImageSpec.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "PARTIAL_UPDATE_IMAGE_SPEC_REJECTED"
      })

      .addCase(deleteImageSpec.pending, (state) => {
        state.loading = true
        state.error = null
        state.lastAction = "DELETE_IMAGE_SPEC_PENDING"
      })
      .addCase(deleteImageSpec.fulfilled, (state, action) => {
        state.loading = false
        state.imageSpecs = state.imageSpecs.filter(
          (spec) => spec.id !== action.payload
        )
        if (
          state.currentImageSpec &&
          state.currentImageSpec.id === action.payload
        ) {
          state.currentImageSpec = null
        }
        state.lastAction = "DELETE_IMAGE_SPEC_FULFILLED"
      })
      .addCase(deleteImageSpec.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastAction = "DELETE_IMAGE_SPEC_REJECTED"
      })
  }
})

export const {
  clearCurrentGuest,
  clearErrors,
  setLastAction,
  clearCurrentImageSpec
} = externalManagementSlice.actions
export default externalManagementSlice.reducer
